import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { fetchInspections, inspectionImageUrl } from '../services/api';
import { IconChevronRight } from '../components/Icons';
import { useCan } from '../hooks/useCan';
import './DataPage.css';

function titleOf(row) {
  return (
    row.meta?.pdr ||
    row.meta?.customerName ||
    'ไม่ระบุ PDR / บริษัท'
  );
}

function sizeLabel(meta = {}) {
  if (meta.sizeW && meta.sizeH) return `${meta.sizeW}×${meta.sizeH} mm`;
  return meta.size || '';
}

export default function DataPage() {
  const [searchParams] = useSearchParams();
  const canExportCoa = useCan('inspections.export_coa');
  const customerId = searchParams.get('customerId') || '';
  const customerName = searchParams.get('customerName') || '';

  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchInspections(100, customerId ? { customerId } : {})
      .then((data) => {
        if (alive) setRows(data || []);
      })
      .catch((err) => {
        if (alive) setError(err.message || 'โหลดข้อมูลไม่สำเร็จ');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [customerId]);

  const title = useMemo(() => {
    if (customerId && customerName) return customerName;
    if (customerId) return 'ลูกค้าที่เลือก';
    return 'ข้อมูล';
  }, [customerId, customerName]);

  return (
    <div className="data-page stack">
      <header className="data-hero">
        <div>
          <p className="data-kicker">ประวัติการตรวจ</p>
          <h1>{title}</h1>
          <p className="muted data-sub">
            {customerId ? 'ผลสแกนของลูกค้านี้' : 'ผลสแกนที่บันทึกไว้ทั้งหมด'}
          </p>
        </div>
        {customerId ? (
          <Link className="btn btn-ghost" to="/data">
            ดูทั้งหมด
          </Link>
        ) : null}
      </header>

      <section className="card data-list-card">
        {loading && <p className="muted">กำลังโหลด...</p>}
        {error && <p style={{ color: 'var(--ng)', margin: 0 }}>{error}</p>}
        {!loading && !error && rows.length === 0 && (
          <div className="data-empty">
            <p className="muted" style={{ margin: 0 }}>ยังไม่มีรายการ</p>
            <Link className="btn btn-primary" to="/scan">ไปสแกน</Link>
          </div>
        )}

        <div className="data-list">
          {rows.map((row) => {
            const img = inspectionImageUrl(row);
            const count = row.spotCount ?? row.spots?.length ?? 0;
            return (
              <article key={row.id} className="data-row">
                <Link to={`/scan/result/${row.id}`} className="data-row-main">
                  {img ? (
                    <img src={img} alt="" className="data-thumb" />
                  ) : (
                    <div className="data-thumb data-thumb-empty" aria-hidden="true" />
                  )}
                  <div className="data-row-body">
                    <div className="data-row-top">
                      <strong>{titleOf(row)}</strong>
                      <span className={`badge ${row.verdict === 'OK' ? 'ok' : 'ng'}`}>
                        {row.verdict === 'OK' ? 'ผ่าน' : 'ไม่ผ่าน'}
                      </span>
                    </div>
                    <p className="muted data-row-meta">
                      {new Date(row.createdAt).toLocaleString()}
                      {row.meta?.customerName ? ` · ${row.meta.customerName}` : ''}
                      {row.meta?.grade ? ` · ${row.meta.grade}` : ''}
                      {sizeLabel(row.meta) ? ` · ${sizeLabel(row.meta)}` : ''}
                      {` · ${count} จุด`}
                    </p>
                  </div>
                </Link>
                {canExportCoa ? (
                <Link
                  className="btn data-coa-btn"
                  to={`/scan/result/${row.id}/coa`}
                >
                  ออก COA
                </Link>
                ) : null}
                <Link
                  to={`/scan/result/${row.id}`}
                  className="data-chevron"
                  aria-label="ดูรายละเอียด"
                >
                  <IconChevronRight size={18} />
                </Link>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
